import { Suspense } from 'react';
import { type DateRange } from 'react-day-picker';
import { FormattedMessage } from 'react-intl';

import { type DateRangePreset } from '@/components/ui/date-range-picker';
import { HireStats } from '@/components/ui/hire-stats';
import { Typography } from '@/components/ui/typography';
import { useOpeningAnalytics } from '@/contexts/opening-context';
import { cn } from '@/utils/classnames';

import { AggregateHiresControls } from '../aggregate-hires-controls';
import { DateRangeDisplay } from './display';

interface DateRangeViewProps {
  appliedDate: DateRange;
  appliedPreset: DateRangePreset;
  onApply: (params: { preset?: DateRangePreset; range: DateRange }) => void;
  openingSlug: string;
  /** True while a newly-applied date range/preset is still being fetched. */
  isPending?: boolean;
  /** The opening's IANA zone, labelled beside the dates when metrics count its local days. */
  timeZone?: string;
}

function DateRangeHires({ openingSlug }: { openingSlug: string }) {
  const { analyticsData } = useOpeningAnalytics();

  return (
    <HireStats
      stats={{
        actual: analyticsData.hire_stats.actual.total,
        expected: analyticsData.hire_stats.expected,
      }}
      typographyVariant="large2"
      openingSlug={openingSlug}
      showOnlyActual
    />
  );
}

function Divider() {
  return <div className="bg-fx-neutral-200 w-px self-stretch" aria-hidden />;
}

export function DateRangeView({
  appliedDate,
  appliedPreset,
  onApply,
  openingSlug,
  isPending,
  timeZone,
}: DateRangeViewProps) {
  return (
    <div className="flex min-h-[80px] items-start gap-6">
      <DateRangeDisplay
        appliedDate={appliedDate}
        appliedPreset={appliedPreset}
        onApply={onApply}
        isPending={isPending}
        timeZone={timeZone}
      />

      <Divider />

      <div
        className={cn(
          'flex min-w-0 flex-1 flex-col gap-1 transition-opacity',
          isPending && 'opacity-50',
        )}
      >
        <Typography variant="caption">
          <FormattedMessage
            defaultMessage="Hires"
            description="Caption above hire count when filtering metrics by date range"
          />
        </Typography>
        <Suspense fallback={<HireStats.Skeleton />}>
          <DateRangeHires openingSlug={openingSlug} />
        </Suspense>
        <AggregateHiresControls />
      </div>
    </div>
  );
}
